import { useMemo, useState } from "react"
import { Checkbox } from "@nova/component-checkbox"
import { ClickAwayListener, ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import { Input, InputOutlined, TextArea } from "@nova/ds-component-input"
import { Select } from "@nova/component-select"
import { ScrollableArea } from "@nova/component-scrollable-area"
import * as Icons from "@nova/component-icons"
import { getDroneModelKeyLabel, resolveDroneModelKey } from "@nova/constants"
import { useAircraft } from "features/pilot-tracking/aircraft/AircraftQueries"
import { pluralize } from "@nova/util-strings"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { updateForm, selectForm } from "features/pilot-tracking/maintenance/slice"
import classNames from "classnames"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"

const STATUS_OPTIONS = [
    { label: "Good to Fly", value: "good_to_fly" },
    { label: "Needs Maintenance", value: "needs_maintenance" },
]

function AircraftMultiSelect({
    value,
    onChange,
    aircraft,
    applyToAll,
    onApplyToAllChange,
}: {
    value: string[]
    onChange: (ids: string[]) => void
    aircraft: ReturnType<typeof useAircraft>["aircraft"]
    applyToAll: boolean
    onApplyToAllChange: (v: boolean) => void
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState("")

    const filtered = aircraft.filter((a) => {
        const label = a.nickname || a.model || a.manufacturer
        return label.toLowerCase().includes(search.toLowerCase())
    })

    const triggerLabel = applyToAll
        ? "All Drones"
        : pluralize({ count: value.length, str: "Drone" })

    const toggle = (id: string) => {
        if (value.includes(id)) {
            onChange(value.filter((v) => v !== id))
        } else {
            onChange([...value, id])
        }
    }

    return (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <div className="relative w-full">
                <button
                    type="button"
                    className={`flex justify-between items-center border pl-4 py-2 pr-2 text-left w-full rounded-lg bg-gray-700 ${isOpen ? "border-blue-200" : "border-transparent"}`}
                    onClick={() => setIsOpen((o) => !o)}
                >
                    <span className="text-gray-0">{triggerLabel}</span>
                    {isOpen ? (
                        <Icons.ArrowDropUp className="text-gray-300" />
                    ) : (
                        <Icons.ArrowDropdown className="text-gray-300" />
                    )}
                </button>
                {isOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-gray-700 rounded-md border border-gray-700 p-4 pb-1 shadow-xl">
                        <div className="flex items-center gap-3 ">
                            <span className="text-gray-300 text-sm shrink-0">Search</span>
                            <InputOutlined
                                placeholder="Search all assets"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <button
                                type="button"
                                className="text-white text-sm whitespace-nowrap shrink-0"
                                onClick={() => {
                                    if (applyToAll) {
                                        onApplyToAllChange(false)
                                        onChange([])
                                    } else {
                                        onApplyToAllChange(true)
                                        onChange([])
                                    }
                                }}
                            >
                                {applyToAll ? "Deselect All" : "Select All"}
                            </button>
                        </div>
                        {applyToAll ? (
                            <div className="py-3 text-sm text-gray-300">
                                Applies to all drones, including future ones.
                            </div>
                        ) : (
                            <ScrollableArea className="flex flex-col max-h-48">
                                {filtered.map((a) => {
                                    const label = a.nickname || a.model || a.manufacturer
                                    return (
                                        <div
                                            key={a.id}
                                            className="flex items-center gap-3 py-2 cursor-pointer"
                                            onClick={() => toggle(a.id)}
                                        >
                                            <Checkbox
                                                isChecked={value.includes(a.id)}
                                                onClick={() => toggle(a.id)}
                                            />
                                            <span className="text-white text-sm">{label}</span>
                                        </div>
                                    )
                                })}
                            </ScrollableArea>
                        )}
                    </div>
                )}
            </div>
        </ClickAwayListener>
    )
}

function useWorkspaceDroneModels(
    selected: string[],
    aircraft: ReturnType<typeof useAircraft>["aircraft"]
) {
    return useMemo(() => {
        const keys = new Set<string>(selected)
        for (const a of aircraft) {
            const key = resolveDroneModelKey(a.manufacturer, a.model)
            if (key) keys.add(key)
        }
        return [...keys]
            .map((key) => ({ value: key, label: getDroneModelKeyLabel(key) }))
            .toSorted((a, b) => a.label.localeCompare(b.label))
    }, [aircraft, selected])
}

function DroneModelMultiSelect({
    value,
    onChange,
    aircraft,
    disabled,
}: {
    value: string[]
    onChange: (keys: string[]) => void
    aircraft: ReturnType<typeof useAircraft>["aircraft"]
    disabled: boolean
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState("")
    const models = useWorkspaceDroneModels(value, aircraft)

    const filtered = models.filter((m) => m.label.toLowerCase().includes(search.toLowerCase()))

    const triggerLabel =
        value.length === 0
            ? "No models"
            : value.length === 1
              ? getDroneModelKeyLabel(value[0])
              : `${value.length} models`

    const toggle = (key: string) => {
        if (value.includes(key)) {
            onChange(value.filter((v) => v !== key))
        } else {
            onChange([...value, key])
        }
    }

    const allFilteredSelected =
        filtered.length > 0 && filtered.every((m) => value.includes(m.value))

    const toggleAll = () => {
        if (allFilteredSelected) {
            const filteredKeys = new Set(filtered.map((m) => m.value))
            onChange(value.filter((v) => !filteredKeys.has(v)))
        } else {
            onChange([...new Set([...value, ...filtered.map((m) => m.value)])])
        }
    }

    return (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <div className="relative w-full">
                <button
                    type="button"
                    disabled={disabled}
                    className={`flex justify-between items-center border pl-4 py-2 pr-2 text-left w-full rounded-lg bg-gray-700 disabled:opacity-50 ${isOpen ? "border-blue-200" : "border-transparent"}`}
                    onClick={() => setIsOpen((o) => !o)}
                >
                    <span className="text-gray-0">{disabled ? "All Drones" : triggerLabel}</span>
                    {isOpen ? (
                        <Icons.ArrowDropUp className="text-gray-300" />
                    ) : (
                        <Icons.ArrowDropdown className="text-gray-300" />
                    )}
                </button>
                {isOpen && !disabled && (
                    <div className="absolute z-50 mt-1 w-full bg-gray-700 rounded-md border border-gray-700 p-4 pb-1 shadow-xl">
                        <div className="flex items-center gap-3">
                            <span className="text-gray-300 text-sm shrink-0">Search</span>
                            <InputOutlined
                                placeholder="Search models"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <button
                                type="button"
                                className="text-white text-sm whitespace-nowrap shrink-0"
                                onClick={toggleAll}
                            >
                                {allFilteredSelected ? "Deselect All" : "Select All"}
                            </button>
                        </div>
                        {filtered.length === 0 ? (
                            <div className="py-3 text-sm text-gray-300">No drone models found.</div>
                        ) : (
                            <ScrollableArea className="flex flex-col max-h-48">
                                {filtered.map((m) => (
                                    <div
                                        key={m.value}
                                        className="flex items-center gap-3 py-2 cursor-pointer"
                                        onClick={() => toggle(m.value)}
                                    >
                                        <Checkbox
                                            isChecked={value.includes(m.value)}
                                            onClick={() => toggle(m.value)}
                                        />
                                        <span className="text-white text-sm">{m.label}</span>
                                    </div>
                                ))}
                            </ScrollableArea>
                        )}
                    </div>
                )}
            </div>
        </ClickAwayListener>
    )
}

const FormRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-start gap-6">
        <div className="w-40 min-w-40 pt-2">
            <h5 className="text-white">{label}</h5>
        </div>
        <div className="flex-1">{children}</div>
    </div>
)

const ConditionRow = ({
    label,
    enabled,
    onToggle,
    value,
    onChange,
    unit,
}: {
    label: string
    enabled: boolean
    onToggle: () => void
    value: number | null
    onChange: (v: number | null) => void
    unit: string
}) => (
    <div className="flex items-center gap-3">
        <Checkbox isChecked={enabled} onClick={onToggle} />
        <h5 className="text-gray-100 w-20">{label}</h5>
        <div className="w-24">
            <Input
                inputMode="numeric"
                value={value?.toString() ?? ""}
                onChange={(e) => {
                    const n = Number(e.target.value)
                    if (!isNaN(n)) {
                        onChange(n)
                    }
                }}
                disabled={!enabled}
            />
        </div>
        <h5 className="text-gray-300">{unit}</h5>
    </div>
)

export function MaintenanceTemplateForm() {
    const { aircraft } = useAircraft()
    const dispatch = useAppDispatch()
    const {
        name,
        description,
        instructions,
        statusOnTrigger,
        aircraftIds,
        modelTargets,
        applyToAll,
        usage,
        duration,
        interval,
    } = useAppSelector(selectForm)
    const [editing, setEditing] = useState<"none" | "title" | "description">("none")

    return (
        <div className="py-6 flex justify-center">
            <div className="flex flex-col w-full max-w-2xl gap-6">
                <div className="flex flex-col w-full">
                    <ClickAwayListenerV2
                        onClickAway={() => {
                            if (editing === "title") setEditing("none")
                        }}
                    >
                        <div
                            className={classNames("w-full", {
                                "cursor-text": editing !== "title",
                            })}
                            onClick={() => {
                                if (editing === "title") return
                                setEditing("title")
                            }}
                        >
                            <RenamableText
                                Tag="h2"
                                id="maintenance-template-name"
                                title={name || "Untitled Maintenance Template"}
                                initialTitleIsPlaceholder={!name}
                                isRenaming={editing === "title"}
                                className="[&_h2]:text-[28px] [&_h2]:text-white"
                                onConfirmRenaming={(_, v) => {
                                    dispatch(updateForm({ key: "name", value: v }))
                                    setEditing("none")
                                }}
                                onCancelRenaming={() => {
                                    setEditing("none")
                                }}
                            />
                        </div>
                    </ClickAwayListenerV2>
                    <ClickAwayListenerV2
                        onClickAway={() => {
                            if (editing === "description") setEditing("none")
                        }}
                    >
                        <div
                            className={classNames("w-full", {
                                "cursor-text": editing !== "description",
                            })}
                            onClick={() => {
                                if (editing === "description") return
                                setEditing("description")
                            }}
                        >
                            <RenamableText
                                Tag="h5"
                                id="maintenance-template-description"
                                title={description || "Add a description"}
                                initialTitleIsPlaceholder={!description}
                                isRenaming={editing === "description"}
                                className={classNames({
                                    "[&_h5]:text-gray-300": editing !== "description",
                                    "[&_h5]:text-white": editing === "description",
                                })}
                                onConfirmRenaming={(_, v) => {
                                    dispatch(updateForm({ key: "description", value: v }))
                                    setEditing("none")
                                }}
                                onCancelRenaming={() => {
                                    setEditing("none")
                                }}
                            />
                        </div>
                    </ClickAwayListenerV2>
                </div>
                <div className="w-full h-px bg-gray-700" />
                <div className="py-6 flex flex-col gap-6">
                    <FormRow label="Apply To*">
                        <AircraftMultiSelect
                            value={aircraftIds}
                            onChange={(ids) =>
                                dispatch(updateForm({ key: "aircraftIds", value: ids }))
                            }
                            aircraft={aircraft}
                            applyToAll={applyToAll}
                            onApplyToAllChange={(v) =>
                                dispatch(updateForm({ key: "applyToAll", value: v }))
                            }
                        />
                    </FormRow>
                    <FormRow label="By Model">
                        <DroneModelMultiSelect
                            value={modelTargets}
                            onChange={(keys) =>
                                dispatch(updateForm({ key: "modelTargets", value: keys }))
                            }
                            aircraft={aircraft}
                            disabled={applyToAll}
                        />
                    </FormRow>
                    <FormRow label="Condition(s)*">
                        <div className="flex flex-col gap-3">
                            <ConditionRow
                                label="Usage"
                                enabled={usage.enabled}
                                onToggle={() =>
                                    dispatch(
                                        updateForm({
                                            key: "usage",
                                            value: { ...usage, enabled: !usage.enabled },
                                        })
                                    )
                                }
                                value={usage.value}
                                onChange={(v) =>
                                    dispatch(
                                        updateForm({ key: "usage", value: { ...usage, value: v } })
                                    )
                                }
                                unit="flights"
                            />
                            <ConditionRow
                                label="Duration"
                                enabled={duration.enabled}
                                onToggle={() =>
                                    dispatch(
                                        updateForm({
                                            key: "duration",
                                            value: { ...duration, enabled: !duration.enabled },
                                        })
                                    )
                                }
                                value={duration.value}
                                onChange={(v) =>
                                    dispatch(
                                        updateForm({
                                            key: "duration",
                                            value: { ...duration, value: v },
                                        })
                                    )
                                }
                                unit="hours"
                            />
                            <ConditionRow
                                label="Interval"
                                enabled={interval.enabled}
                                onToggle={() =>
                                    dispatch(
                                        updateForm({
                                            key: "interval",
                                            value: { ...interval, enabled: !interval.enabled },
                                        })
                                    )
                                }
                                value={interval.value}
                                onChange={(v) =>
                                    dispatch(
                                        updateForm({
                                            key: "interval",
                                            value: { ...interval, value: v },
                                        })
                                    )
                                }
                                unit="days"
                            />
                        </div>
                    </FormRow>
                    <FormRow label="Instructions">
                        <TextArea
                            placeholder="List inspection steps, replacement parts, or calibration tasks for this service."
                            value={instructions}
                            onChange={(e) =>
                                dispatch(
                                    updateForm({
                                        key: "instructions",
                                        value: e.currentTarget.value,
                                    })
                                )
                            }
                            rows={5}
                        />
                    </FormRow>
                    <FormRow label="Status when due">
                        <Select
                            onChange={(v: string) =>
                                dispatch(updateForm({ key: "statusOnTrigger", value: v }))
                            }
                            options={STATUS_OPTIONS}
                            value={statusOnTrigger}
                        />
                    </FormRow>
                </div>
            </div>
        </div>
    )
}
